import React from 'react';
import { 
  ClipboardList, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  ArrowRight,
  Settings,
  Plus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

const workflowTemplates = [
  {
    id: 1,
    name: 'Expense Approval',
    description: 'Multi-level approval for expenses above threshold',
    steps: 3,
    active: true,
    triggers: 12,
    testId: 'workflow-expense',
  },
  {
    id: 2,
    name: 'Purchase Order',
    description: 'Approval workflow for purchase orders',
    steps: 2,
    active: true,
    triggers: 28,
    testId: 'workflow-po',
  },
  {
    id: 3,
    name: 'Leave Request',
    description: 'Staff leave and time-off approvals',
    steps: 2,
    active: true,
    triggers: 8,
    testId: 'workflow-leave',
  },
  {
    id: 4,
    name: 'Menu Price Change',
    description: 'Approval for menu item price modifications',
    steps: 4,
    active: false,
    triggers: 0,
    testId: 'workflow-menu',
  },
];

const recentWorkflows = [
  {
    id: 'WF-001',
    type: 'Expense Approval',
    item: 'Coffee Machine - kr 55 000',
    status: 'pending',
    step: 2,
    totalSteps: 3,
    requester: 'John Smith',
    timestamp: '2 hours ago',
  },
  {
    id: 'WF-002',
    type: 'Purchase Order',
    item: 'Weekly Produce Order - kr 12 500',
    status: 'approved',
    step: 2,
    totalSteps: 2,
    requester: 'Sarah Johnson',
    timestamp: '5 hours ago',
  },
  {
    id: 'WF-003',
    type: 'Leave Request',
    item: 'Annual Leave - 5 days',
    status: 'rejected',
    step: 1,
    totalSteps: 2,
    requester: 'Mike Wilson',
    timestamp: '1 day ago',
  },
];

const statusConfig = {
  pending: { icon: Clock, color: 'text-spice-turmeric', bg: 'bg-spice-turmeric/10', label: 'Pending' },
  approved: { icon: CheckCircle, color: 'text-spice-cardamom', bg: 'bg-spice-cardamom/10', label: 'Approved' },
  rejected: { icon: XCircle, color: 'text-spice-tandoori', bg: 'bg-spice-tandoori/10', label: 'Rejected' },
};

export function Workflows() {
  return (
    <div data-testid="workflows-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-brand-charcoal">Workflows & Approvals</h1>
          <p className="text-gray-500 text-sm mt-1">Manage approval workflows and automation</p>
        </div>
        <Button data-testid="create-workflow-btn" className="bg-brand-orange hover:bg-brand-orange/90 gap-2">
          <Plus className="w-4 h-4" />
          Create Workflow
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-orange/10 rounded-lg">
                <ClipboardList className="w-5 h-5 text-brand-orange" />
              </div>
              <div>
                <p className="text-2xl font-semibold font-mono">4</p>
                <p className="text-sm text-gray-500">Active Workflows</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-spice-turmeric/10 rounded-lg">
                <Clock className="w-5 h-5 text-spice-turmeric" />
              </div>
              <div>
                <p className="text-2xl font-semibold font-mono">12</p>
                <p className="text-sm text-gray-500">Pending Approvals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-spice-cardamom/10 rounded-lg">
                <CheckCircle className="w-5 h-5 text-spice-cardamom" />
              </div>
              <div>
                <p className="text-2xl font-semibold font-mono">48</p>
                <p className="text-sm text-gray-500">Approved (30d)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-spice-tandoori/10 rounded-lg">
                <XCircle className="w-5 h-5 text-spice-tandoori" />
              </div>
              <div>
                <p className="text-2xl font-semibold font-mono">3</p>
                <p className="text-sm text-gray-500">Rejected (30d)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Templates */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Workflow Templates</CardTitle>
              <CardDescription>Configure and manage approval workflows</CardDescription>
            </div>
            <Button variant="outline" size="sm" className="gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {workflowTemplates.map((workflow) => (
              <div
                key={workflow.id}
                data-testid={workflow.testId}
                className={cn(
                  'p-4 border rounded-lg transition-all cursor-pointer',
                  'hover:border-brand-orange hover:shadow-md',
                  !workflow.active && 'opacity-60'
                )}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-brand-charcoal">{workflow.name}</h3>
                      <Badge variant={workflow.active ? 'default' : 'secondary'} className={workflow.active ? 'bg-spice-cardamom' : ''}>
                        {workflow.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{workflow.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                  <span>{workflow.steps} steps</span>
                  <span>•</span>
                  <span>{workflow.triggers} triggers this month</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Workflows */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Workflow Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentWorkflows.map((workflow) => {
              const status = statusConfig[workflow.status];
              const StatusIcon = status.icon;
              
              return (
                <div
                  key={workflow.id}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg"
                >
                  <div className={cn('p-2 rounded-lg', status.bg)}>
                    <StatusIcon className={cn('w-5 h-5', status.color)} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-gray-400">{workflow.id}</span>
                      <span className="font-medium text-brand-charcoal">{workflow.type}</span>
                    </div>
                    <p className="text-sm text-gray-600">{workflow.item}</p>
                    <p className="text-xs text-gray-400 mt-1">by {workflow.requester} • {workflow.timestamp}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={status.bg + ' ' + status.color + ' border-0'}>
                      {status.label}
                    </Badge>
                    {workflow.status === 'pending' && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Step {workflow.step}/{workflow.totalSteps}</p>
                        <Progress value={(workflow.step / workflow.totalSteps) * 100} className="h-1.5 w-24" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
